'use strict';

import { flow, observable, configure, action, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {sleep} from "@uw-it-sis/lib-react/lib/WaitUtils";

import api from '../lib/Api';

/**
 * Store for Audit status.
 */
class AuditStatusStore {
    // State object
    state = States.initial;

    operationData = {
        description: "",
        recentlyOperated: false
    };

    constructor() {
        makeObservable(this, {
            state: observable,
            operationData: observable,
            setRecentlyOperated: action,
            reloadStatus: action
        });

        this.status = undefined;
        this.lockedSyskeys = [];
        this.exceptionUtils = appStore.getExceptionUtils();
    }

    /*
     * Makes a post request to the given url.
     */
    _sendPost = flow(function* (action, operationDescription, data) {

        this.state = States.pending;

        this.operationData.description = operationDescription;

        try {
            if (action === 'pausePoller') {
                this.status = yield api.pauseAuditListenerPoller();
            } else if (action === 'resumePoller') {
                this.status = yield api.resumeAuditListenerPoller();
            } else if (action === 'clear') {
                this.status = yield api.clearErrorsAuditListener();
            } else if (action === 'pauseProcessor') {
                this.status = yield api.pauseAuditListenerProcessor();
            } else if (action === 'resumeProcessor') {
                this.status = yield api.resumeAuditListenerProcessor();
            }

            // only set the recently operated flag if the operation succeeded
            this.setRecentlyOperatedTemporarily();
        } catch (error) {
            this.exceptionUtils.handleException(error);
        }

        this.state = States.done;

    }.bind(this));

    setRecentlyOperatedTemporarily = flow (async function* () {

        this.setRecentlyOperated(true);

        yield new Promise(resolve => resolve()).then();

        await sleep(2000);

        this.setRecentlyOperated(false);
    }.bind(this));

    setRecentlyOperated(newValue) {
        this.operationData.recentlyOperated = newValue;
    }

    pausePoller = () => this._sendPost('pausePoller', 'pausing poller ');
    resumePoller = () => this._sendPost('resumePoller', 'resuming poller ');
    clear = () => this._sendPost('clear', 'clearing errors ');
    pauseProcessor = () => this._sendPost('pauseProcessor', 'pausing processor ');
    resumeProcessor = () => this._sendPost('resumeProcessor', 'resuming processor ');

    loadStatus = flow(function* () {
        // Update the aggregate state to pending.
        this.state = States.pending;
        try {
            this.status = yield api.getAuditListenerStatus();
            if (appStore.isDebug()) {
                console.log("AuditStatusStore: Data load complete.");
            }
            this.state = States.done;
        } catch (error) {
            this.state = States.error;
        }
    }.bind(this));

    reloadStatus() {
        this.operationData.description = "refreshing status";

        this.loadStatus();

        this.setRecentlyOperatedTemporarily();
    }

     cleanupLockedRecord = flow(function* (syskey) {
        this.state = States.pending;
        try {
             yield api.cleanupLockedRecord(syskey);
            if (appStore.isDebug()) {
                console.log("AuditStatusStore: Cleanup complete.");
            }
            this.state = States.done;
        } catch (error) {
            this.state = States.error;
        }
    }.bind(this));

    getLockedSyskeys = flow(function* () {
        this.state = States.pending;
        try {
            this.lockedSyskeys = yield api.getLockedSyskeys();
            if (appStore.isDebug()) {
                console.log("AuditStatusStore: getLockedSyskeys complete.");
            }
            this.state = States.done;
        } catch (error) {
            this.state = States.error;
        }
    }.bind(this));

    getStatus = () => { return this.status }

    hasData = () => { return this.status !== undefined }
}

export default new AuditStatusStore();
